import React, { useEffect, useMemo, useRef, useState } from "react";
import { alpha } from "@mui/material/styles";
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  Stack,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Tooltip,
  Collapse,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventSeatIcon from "@mui/icons-material/EventSeat";
import DirectionsBusOutlinedIcon from "@mui/icons-material/DirectionsBusOutlined";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  buildTripDisplayGroups,
  classeComfortRank,
  classePitch,
  computeDurationMins,
  filterEntriesByClasse,
  filterEntriesByPeriod,
  formatDurationMins,
  formatTripPriceBRL,
  listAvailableTripClasses,
  normalizeTripClasse,
  parseTripPrice,
  pickRecommendedTrip,
  sortTripEntries,
  tripListHighlights,
  tripHasConexao,
  tripItineraryLabel,
  tripConnectionCity,
  tripConnectionLegs,
} from "./bpeTripDisplay";

const QUICK_FILTERS = ["all", "manha", "tarde", "noite"];

function SelectionDot({ selected }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: 18,
        height: 18,
        borderRadius: "50%",
        border: 2,
        borderColor: selected ? "primary.main" : "grey.400",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {selected ? (
        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "primary.main" }} />
      ) : null}
    </Box>
  );
}

function TripClassRow({
  item,
  selectedOption,
  onSelect,
  passengerCount,
  bestPrice,
  maxComfortInGroup,
  t,
  cardRefs,
  compactHeader,
  showSchedule,
}) {
  const { viagem: v, apiIndex } = item;
  const actualOption = String(apiIndex + 1);
  const selected = selectedOption === actualOption;
  const price = parseTripPrice(v.valor);
  const seatsNum = v.poltronas_disponiveis != null ? Number(v.poltronas_disponiveis) : null;
  const classe = normalizeTripClasse(v.classe);
  const pitch = classePitch(classe);
  const isCheapest = bestPrice != null && price === bestPrice;
  const isScarce = seatsNum != null && seatsNum > 0 && seatsNum <= 4;
  const isLow = seatsNum != null && seatsNum > 4 && seatsNum <= 12;
  const rank = classeComfortRank(classe);
  const isPremium =
    maxComfortInGroup > rank && maxComfortInGroup === classeComfortRank(classe) && compactHeader;
  const notEnoughSeats =
    seatsNum != null && Number.isFinite(seatsNum) && seatsNum < passengerCount;
  const totalForParty = price != null && passengerCount > 1 ? price * passengerCount : null;
  const dur = computeDurationMins(v.inicio, v.fim);

  const row = (
    <CardActionArea
      onClick={() => onSelect(actualOption)}
      sx={{
        display: "block",
        borderRadius: 0,
        ...(selected && {
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
        }),
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.25,
          py: compactHeader ? 0.65 : 0.85,
          borderTop: compactHeader ? 1 : 0,
          borderColor: "divider",
        }}
      >
        <SelectionDot selected={selected} />
        <Box flex={1} minWidth={0}>
          {showSchedule && (
            <Typography variant="body2" fontWeight={700} noWrap>
              {v.inicio} → {v.fim}
              {dur != null ? (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                  ({formatDurationMins(dur)})
                </Typography>
              ) : null}
            </Typography>
          )}
          {tripHasConexao(v) ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.25 }}>
              {tripItineraryLabel(v) ||
                t("bpeWizard.connectionVia", { city: tripConnectionCity(v) || "—" })}
              {tripConnectionLegs(v)
                .filter((leg) => leg?.horaSaida)
                .map((leg, i) => ` · ${i + 1}º ${leg.horaSaida}${leg.horaChegada ? `–${leg.horaChegada}` : ""}`)
                .join("")}
            </Typography>
          ) : null}
          <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.4} sx={{ mt: showSchedule ? 0.25 : 0 }}>
            {tripHasConexao(v) ? (
              <Chip
                label={t("bpeWizard.withConnection")}
                size="small"
                color="info"
                variant="outlined"
                sx={{ height: 20, "& .MuiChip-label": { px: 0.75, fontSize: "0.65rem", fontWeight: 700 } }}
              />
            ) : null}
            {classe ? (
              <Tooltip title={pitch || ""} disableHoverListener={!pitch}>
                <Chip
                  label={classe}
                  size="small"
                  color={isPremium ? "warning" : "default"}
                  variant={isPremium ? "filled" : "outlined"}
                  sx={{ height: 22, "& .MuiChip-label": { px: 0.75, fontSize: "0.7rem", fontWeight: 600 } }}
                />
              </Tooltip>
            ) : null}
            {isCheapest ? (
              <Chip
                label={t("bpeWizard.cheapest")}
                size="small"
                color="success"
                variant="outlined"
                sx={{ height: 20, fontSize: "0.65rem" }}
              />
            ) : null}
            {seatsNum != null ? (
              <Chip
                label={
                  isScarce
                    ? t("bpeWizard.seatsScarce", { count: seatsNum })
                    : `${seatsNum}`
                }
                size="small"
                color={notEnoughSeats ? "default" : isScarce ? "error" : isLow ? "warning" : "default"}
                variant="outlined"
                sx={{ height: 20, minWidth: 28, "& .MuiChip-label": { px: 0.5, fontSize: "0.65rem" } }}
              />
            ) : null}
            {notEnoughSeats ? (
              <Typography variant="caption" color="error.main" sx={{ fontSize: "0.65rem" }}>
                {t("bpeWizard.seatsNotEnoughShort", { need: passengerCount })}
              </Typography>
            ) : null}
          </Stack>
        </Box>
        <Box
          sx={{
            flexShrink: 0,
            textAlign: "right",
            minWidth: 72,
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            bgcolor: (theme) =>
              isCheapest ? alpha(theme.palette.success.main, 0.12) : "transparent",
          }}
        >
          <Typography
            variant="body1"
            color={isCheapest ? "success.dark" : "primary.main"}
            fontWeight={800}
            lineHeight={1.1}
            sx={{ fontSize: "0.95rem" }}
          >
            {formatTripPriceBRL(v.valor)}
          </Typography>
          {totalForParty != null && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem", lineHeight: 1.2 }}>
              {passengerCount}× {totalForParty.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
            </Typography>
          )}
        </Box>
      </Box>
    </CardActionArea>
  );

  return (
    <Box
      ref={(el) => {
        if (el) cardRefs.current[actualOption] = el;
      }}
    >
      {row}
    </Box>
  );
}

const BPeTripOptionList = ({
  viagens = [],
  selectedOption = "",
  onSelect,
  passengerCount = 1,
  t,
}) => {
  const [sortMode, setSortMode] = useState("time");
  const [periodFilter, setPeriodFilter] = useState("all");
  const [classeFilter, setClasseFilter] = useState("all");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const cardRefs = useRef({});

  const entries = useMemo(
    () => viagens.map((viagem, apiIndex) => ({ viagem, apiIndex })),
    [viagens]
  );

  const availableClasses = useMemo(() => listAvailableTripClasses(viagens), [viagens]);
  const hasMultipleClasses = availableClasses.length > 1;

  const filteredEntries = useMemo(() => {
    let list = sortTripEntries(entries, sortMode);
    list = filterEntriesByPeriod(list, periodFilter);
    list = filterEntriesByClasse(list, classeFilter);
    return list;
  }, [entries, sortMode, periodFilter, classeFilter]);

  const displayGroups = useMemo(
    () => buildTripDisplayGroups(filteredEntries),
    [filteredEntries]
  );

  const recommendation = useMemo(
    () => pickRecommendedTrip(viagens, passengerCount),
    [viagens, passengerCount]
  );
  const recommendedOption =
    recommendation != null ? String(recommendation.apiIndex + 1) : "";

  const { bestPrice } = useMemo(() => tripListHighlights(viagens), [viagens]);

  const recommendedTrip =
    recommendation != null ? viagens[recommendation.apiIndex] : null;
  const recommendedClasse = recommendedTrip ? normalizeTripClasse(recommendedTrip.classe) : null;
  const recommendedReasons = Array.isArray(recommendation?.reasons)
    ? recommendation.reasons.filter((r) => r && r !== "single")
    : [];

  useEffect(() => {
    const el = cardRefs.current[selectedOption];
    if (el?.scrollIntoView) {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [selectedOption]);

  if (!entries.length) return null;

  const hasActiveFilters = periodFilter !== "all" || classeFilter !== "all" || sortMode !== "time";

  const filterChips = (
    <>
      {QUICK_FILTERS.map((key) => (
        <Chip
          key={key}
          size="small"
          label={t(`bpeWizard.period_${key}`)}
          onClick={() => setPeriodFilter(key)}
          color={periodFilter === key ? "primary" : "default"}
          variant={periodFilter === key ? "filled" : "outlined"}
          sx={{ height: 24, flexShrink: 0 }}
        />
      ))}
      <Chip
        size="small"
        label={t("bpeWizard.pickCheapest")}
        onClick={() => {
          setSortMode("price");
          setPeriodFilter("all");
          setClasseFilter("all");
          const idx = viagens.findIndex((v) => parseTripPrice(v.valor) === bestPrice);
          if (idx >= 0) onSelect(String(idx + 1));
        }}
        variant="outlined"
        sx={{ height: 24, flexShrink: 0 }}
      />
      {availableClasses.length > 1 &&
        availableClasses.map((c) => (
          <Chip
            key={c}
            size="small"
            label={c}
            onClick={() => setClasseFilter((prev) => (prev === c ? "all" : c))}
            color={classeFilter === c ? "secondary" : "default"}
            variant={classeFilter === c ? "filled" : "outlined"}
            sx={{ height: 24, flexShrink: 0 }}
          />
        ))}
    </>
  );

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Barra compacta: sugestão + filtros sob demanda */}
      <Box sx={{ flexShrink: 0, mb: 0.5 }}>
        {recommendedTrip && (
          <Box
            sx={{
              mb: 0.5,
              py: 0.4,
              px: 0.75,
              borderRadius: 1,
              border: 1,
              borderColor: "success.light",
              bgcolor: (theme) => alpha(theme.palette.success.main, 0.07),
            }}
          >
            <Stack direction="row" alignItems="center" gap={0.75}>
              <ThumbUpOutlinedIcon color="success" sx={{ fontSize: 15, flexShrink: 0 }} />
              <Typography variant="caption" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
                {t("bpeWizard.suggestionShort", {
                  dep: recommendedTrip.inicio,
                  arr: recommendedTrip.fim,
                  classe: recommendedClasse ? ` · ${recommendedClasse}` : "",
                  price: formatTripPriceBRL(recommendedTrip.valor),
                })}
              </Typography>
              <Button
                size="small"
                variant="contained"
                color="success"
                onClick={() => onSelect(recommendedOption)}
                sx={{ textTransform: "none", minWidth: 0, px: 0.75, py: 0.15, fontSize: "0.7rem", flexShrink: 0 }}
              >
                {t("bpeWizard.useSuggestion")}
              </Button>
            </Stack>
            {recommendedReasons.length > 0 && (
              <Stack direction="row" gap={0.4} flexWrap="wrap" sx={{ mt: 0.4, pl: 2.5 }}>
                {recommendedReasons.map((r) => (
                  <Chip
                    key={r}
                    size="small"
                    label={t(`bpeWizard.suggestionReason.${r}`)}
                    color="success"
                    variant="outlined"
                    sx={{ height: 18, fontSize: "0.62rem", "& .MuiChip-label": { px: 0.6 } }}
                  />
                ))}
              </Stack>
            )}
          </Box>
        )}

        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={0.5}>
          <Stack direction="row" alignItems="center" gap={0.5} minWidth={0}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} noWrap>
              {t("bpeWizard.tripsFound", { count: filteredEntries.length })}
            </Typography>
            {hasMultipleClasses && (
              <Tooltip title={t("bpeWizard.sameBusClassHint")}>
                <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", flexShrink: 0 }} />
              </Tooltip>
            )}
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
            <Button
              size="small"
              variant={filtersOpen || hasActiveFilters ? "contained" : "outlined"}
              startIcon={<FilterListIcon sx={{ fontSize: "16px !important" }} />}
              onClick={() => setFiltersOpen((o) => !o)}
              sx={{ textTransform: "none", py: 0.15, px: 1, fontSize: "0.72rem", minWidth: 0 }}
            >
              {t("bpeWizard.tripFilters")}
              {hasActiveFilters && !filtersOpen ? " •" : ""}
            </Button>
          </Stack>
        </Stack>

        <Collapse in={filtersOpen}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={0.5} sx={{ mt: 0.5, mb: 0.25 }}>
            <Typography variant="caption" color="text.secondary">
              {t("bpeWizard.sortTrips")}
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={sortMode}
              onChange={(_e, v) => v && setSortMode(v)}
              sx={{ flexShrink: 0, "& .MuiToggleButton-root": { py: 0.1, px: 0.75, fontSize: "0.7rem" } }}
            >
              <ToggleButton value="time" sx={{ textTransform: "none" }}>
                {t("bpeWizard.sortByTime")}
              </ToggleButton>
              <ToggleButton value="price" sx={{ textTransform: "none" }}>
                {t("bpeWizard.sortByPrice")}
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              pb: 0.25,
            }}
          >
            {filterChips}
          </Box>
        </Collapse>
      </Box>

      {filteredEntries.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
          {t("bpeWizard.noTripsInPeriod")}
        </Typography>
      ) : (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            pr: 0.25,
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
          }}
        >
          {displayGroups.map((group) => {
            const v0 = group.items[0]?.viagem;
            if (!v0) return null;
            const dur = computeDurationMins(v0.inicio, v0.fim);
            const maxComfort = Math.max(
              ...group.items.map((it) => classeComfortRank(normalizeTripClasse(it.viagem.classe)))
            );

            if (!group.sameBus) {
              const item = group.items[0];
              const actualOption = String(item.apiIndex + 1);
              const selected = selectedOption === actualOption;

              return (
                <Card
                  key={group.key}
                  variant="outlined"
                  sx={{
                    flexShrink: 0,
                    borderColor: selected ? "primary.main" : "divider",
                    borderWidth: selected ? 2 : 1,
                    overflow: "hidden",
                  }}
                >
                  <TripClassRow
                    item={item}
                    selectedOption={selectedOption}
                    onSelect={onSelect}
                    passengerCount={passengerCount}
                    bestPrice={bestPrice}
                    maxComfortInGroup={maxComfort}
                    t={t}
                    cardRefs={cardRefs}
                    compactHeader={false}
                    showSchedule
                  />
                </Card>
              );
            }

            const groupSelected = group.items.some(
              (it) => String(it.apiIndex + 1) === selectedOption
            );

            return (
              <Card
                key={group.key}
                variant="outlined"
                sx={{
                  flexShrink: 0,
                  borderColor: groupSelected ? "primary.main" : "divider",
                  borderWidth: groupSelected ? 2 : 1,
                  overflow: "hidden",
                }}
              >
                <Box sx={{ px: 1.25, py: 0.65, bgcolor: "action.hover" }}>
                  <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.5}>
                    <Typography variant="body2" fontWeight={700}>
                      {v0.inicio} → {v0.fim}
                    </Typography>
                    {dur != null && (
                      <Chip
                        icon={<AccessTimeIcon sx={{ fontSize: "12px !important" }} />}
                        label={formatDurationMins(dur)}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, "& .MuiChip-label": { fontSize: "0.65rem", px: 0.5 } }}
                      />
                    )}
                    {tripHasConexao(v0) ? (
                      <Chip
                        label={t("bpeWizard.withConnection")}
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ height: 20, "& .MuiChip-label": { fontSize: "0.65rem", fontWeight: 700 } }}
                      />
                    ) : null}
                    <Chip
                      label={t("bpeWizard.sameBus")}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, "& .MuiChip-label": { fontSize: "0.65rem" } }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ flex: 1, minWidth: 0, display: { xs: "none", sm: "block" } }}
                    >
                      {v0.viacao}
                    </Typography>
                  </Stack>
                </Box>
                {group.items.map((item) => (
                  <TripClassRow
                    key={item.apiIndex}
                    item={item}
                    selectedOption={selectedOption}
                    onSelect={onSelect}
                    passengerCount={passengerCount}
                    bestPrice={bestPrice}
                    maxComfortInGroup={maxComfort}
                    t={t}
                    cardRefs={cardRefs}
                    compactHeader
                  />
                ))}
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default BPeTripOptionList;
